import { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';

/**
 * TokenManager - Centralized token management utility for MSAL authentication
 * 
 * This class provides methods for storing, retrieving, and refreshing authentication tokens
 * used across the application. It supports multiple token types:
 * - Frontend Token: For Microsoft Graph API calls (User.Read scope)
 * - AD Token: For application-specific API calls 
 * - Backend Token: For backend service authentication
 * 
 * Key Features:
 * - Automatic token refresh with MSAL
 * - Retry logic for failed API calls
 * - Consistent token storage in sessionStorage
 * - TypeScript support with proper MSAL types
 * 
 * Usage Examples:
 * 
 * 1. Simple token refresh:
 *    const token = await TokenManager.refreshFrontendToken(msalInstance, account);
 * 
 * 2. Token refresh with retry callback:
 *    const result = await TokenManager.refreshTokenWithRetry(
 *      msalInstance, account, 'frontend', undefined,
 *      async () => { 
 *        // Your API call here 
 *        return await apiCall();
 *      }
 *    );
 * 
 * 3. Check token availability:
 *    if (TokenManager.hasBackendToken()) {
 *      // Token is available
 *    }
 */
export class TokenManager {
  private static readonly FRONTEND_TOKEN_KEY = 'accessTokenP';
  private static readonly BACKEND_TOKEN_KEY = 'accessTokenPB';
  private static readonly AD_TOKEN_KEY = 'accessTokenAD';
  private static readonly ROLE_KEY = 'accessRole';
  private static readonly IMG_URL_KEY = 'imgUrl';
  private static readonly USER_COUNTRY_KEY = 'userCountry';
  private static readonly BACKEND_CLIENT_ID = process.env.REACT_APP_BACKEND_APP_CLIENT_ID;
  private static readonly GRAPH_ME_URL = 'https://graph.microsoft.com/v1.0/me?$select=country';

  // Token retrieval with fallback
  static getFrontendToken(): string | null {
    return sessionStorage.getItem(this.FRONTEND_TOKEN_KEY);
  }

  static getBackendToken(): string | null {
    return sessionStorage.getItem(this.BACKEND_TOKEN_KEY);
  }

  static getAdToken(): string | null {
    return sessionStorage.getItem(this.AD_TOKEN_KEY);
  }

  static getRole(): string {
    return sessionStorage.getItem(this.ROLE_KEY) || 'Global Admin';
  }

  static getImageUrl(): string {
    return sessionStorage.getItem(this.IMG_URL_KEY) || '';
  }

  static getCountry(): string {
    return sessionStorage.getItem(this.USER_COUNTRY_KEY) || '';
  }

  // Token storage
  static setFrontendToken(token: string): void {
    sessionStorage.setItem(this.FRONTEND_TOKEN_KEY, token);
  }

  static setBackendToken(token: string): void {
    sessionStorage.setItem(this.BACKEND_TOKEN_KEY, token);
  }

  static setAdToken(token: string): void {
    sessionStorage.setItem(this.AD_TOKEN_KEY, token);
  }

  static setRole(role: string): void {
    sessionStorage.setItem(this.ROLE_KEY, role);
  }

  static setImageUrl(url: string): void {
    sessionStorage.setItem(this.IMG_URL_KEY, url);
  }

  static setCountry(country: string): void {
    sessionStorage.setItem(this.USER_COUNTRY_KEY, country);
  }

  // Check if the user's country has already been fetched this session
  static hasCountry(): boolean {
    return !!this.getCountry();
  }

  // Clear all tokens
  static clearAll(): void {
    sessionStorage.clear();
  }

  // Check if backend token exists
  static hasBackendToken(): boolean {
    const token = this.getBackendToken();
    return !!(token && token.trim());
  }

  // Wait for backend token with timeout
  static async waitForBackendToken(timeoutMs = 5000): Promise<string> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();
      const checkToken = () => {
        const token = this.getBackendToken();
        if (token && token.trim()) {
          resolve(token);
          return;
        }

        if (Date.now() - startTime >= timeoutMs) {
          reject(new Error('Timeout waiting for backend token'));
          return;
        }

        setTimeout(checkToken, 100);
      };
      checkToken();
    });
  }

  // Initialize all tokens after authentication
  static async initializeTokens(msalInstance: IPublicClientApplication, account: AccountInfo, clientId?: string): Promise<void> {
    try {
      console.log('[initializeTokens] Starting token initialization for all token types');
      
      // Refresh frontend token
      await this.refreshFrontendToken(msalInstance, account);
      
      // Refresh backend token
      await this.refreshBackendToken(msalInstance, account);
      
      // Refresh AD token if clientId is provided
      if (clientId) {
        await this.refreshAdToken(msalInstance, account, clientId);
      }
      
      console.log('[initializeTokens] All tokens initialized successfully');
    } catch (error) {
      alert('[initializeTokens] Failed to initialize tokens: ' + error);
      throw new Error(`Token initialization failed: ${error}`);
    }
  }

  // Ensure backend token is available
  static async ensureBackendToken(msalInstance: IPublicClientApplication, account: AccountInfo): Promise<void> {
    if (!this.hasBackendToken()) {
      console.log('[ensureBackendToken] Backend token missing, attempting to refresh...');
      try {
        await this.refreshBackendToken(msalInstance, account);
      } catch (error) {
        alert('[ensureBackendToken] Failed to refresh backend token: ' + error);
        throw error;
      }
    }
  }

  // Refresh frontend token using MSAL
  static async refreshFrontendToken(msalInstance: IPublicClientApplication, account: AccountInfo): Promise<string> {
    try {
      const request = {
        scopes: ['User.Read'],
        account: account,
      };
      
      const response = await msalInstance.acquireTokenSilent(request);
      console.log('[refreshFrontendToken] Frontend token refreshed successfully');
      const tokenWithBearer = response.accessToken;
      this.setFrontendToken(tokenWithBearer);

      // Also refresh backend token if it doesn't exist
      if (!this.hasBackendToken()) {
        console.log('[refreshFrontendToken] Backend token missing, refreshing...');
        try {
          await this.refreshBackendToken(msalInstance, account);
        } catch (backendError) {
          console.warn('[refreshFrontendToken] Failed to refresh backend token:', backendError);
          // Don't throw here as frontend token was successful
        }
      }

      return tokenWithBearer;
    } catch (error) {
      alert('[refreshFrontendToken] Error: ' + error);
      throw new Error(`Failed to refresh frontend token: ${error}`);
    }
  }

  // Refresh AD token using MSAL
  static async refreshAdToken(msalInstance: IPublicClientApplication, account: AccountInfo, clientId: string): Promise<string> {
    try {
      const request = {
        scopes: [`api://${clientId}/Read_Write`],
        account: account,
      };
      
      const response = await msalInstance.acquireTokenSilent(request);
      this.setAdToken(response.accessToken);
      sessionStorage.setItem(this.AD_TOKEN_KEY, response.accessToken);
      return response.accessToken;
    } catch (error) {
      throw new Error(`Failed to refresh AD token: ${error}`);
    }
  }

  // Refresh backend token using MSAL
  static async refreshBackendToken(msalInstance: IPublicClientApplication, account: AccountInfo): Promise<string> {
    try {
      if (!this.BACKEND_CLIENT_ID) {
        throw new Error('Backend client ID is not configured in environment variables');
      }

      const request = {
        scopes: [`api://${this.BACKEND_CLIENT_ID}/Read_Write`],
        account: account,
      };

      const response = await msalInstance.acquireTokenSilent(request);
      this.setBackendToken(response.accessToken);
      sessionStorage.setItem(this.BACKEND_TOKEN_KEY, response.accessToken);
      console.log('[refreshBackendToken] Backend token refreshed successfully', response.accessToken);
      return response.accessToken;
    } catch (error) {
      throw new Error(`Failed to refresh backend token: ${error}`);
    }
  }

  // Generic token refresh method with retry logic
  static async refreshTokenWithRetry<T>(
    msalInstance: IPublicClientApplication,
    account: AccountInfo,
    tokenType: 'frontend' | 'ad' | 'backend',
    clientId?: string,
    retryCallback?: () => Promise<T>
  ): Promise<T | string> {
    try {
      let refreshedToken: string;
      
      switch (tokenType) {
        case 'frontend':
          refreshedToken = await this.refreshFrontendToken(msalInstance, account);
          break;
        case 'ad':
          if (!clientId) throw new Error('Client ID is required for AD token refresh');
          refreshedToken = await this.refreshAdToken(msalInstance, account, clientId);
          break;
        case 'backend':
          refreshedToken = await this.refreshBackendToken(msalInstance, account);
          break;
        default:
          throw new Error(`Invalid token type: ${tokenType}`);
      }

      // If a retry callback is provided, execute it with the new token
      if (retryCallback) {
        return await retryCallback();
      }

      return refreshedToken;
    } catch (error) {
      throw new Error(`Token refresh failed: ${error}`);
    }
  }

  /**
   * Fetch the signed-in user's country from Microsoft Graph (Azure Entra ID)
   * and cache it in sessionStorage.
   *
   * This is meant to run only the first time the user accesses the application:
   * if the country has already been fetched this session it returns the cached
   * value without making a network call. Uses the frontend (Graph, User.Read)
   * token and refreshes it once if it has expired.
   */
  static async fetchUserCountry(
    msalInstance: IPublicClientApplication,
    account: AccountInfo
  ): Promise<string> {
    // Only fetch the first time the user accesses the application
    if (this.hasCountry()) {
      return this.getCountry();
    }

    const requestCountry = async (token: string | null): Promise<string> => {
      const authHeader = token?.startsWith('Bearer ') ? token : `Bearer ${token}`;
      const response = await fetch(this.GRAPH_ME_URL, {
        headers: { Authorization: authHeader, Accept: 'application/json' },
      });

      const data = await response.json().catch(() => undefined);

      if (!response.ok) {
        const graphError = new Error(
          `Graph /me request failed with status ${response.status}`
        ) as Error & { code?: string };
        graphError.code = data?.error?.code;
        throw graphError;
      }

      // `country` is the directory profile value from Entra ID.
      return data?.country || '';
    };

    try {
      const country = await requestCountry(this.getFrontendToken());
      this.setCountry(country);
      return country;
    } catch (error) {
      // If the frontend token is invalid/expired, refresh it once and retry
      if ((error as { code?: string })?.code === 'InvalidAuthenticationToken') {
        const retryResult = await this.refreshTokenWithRetry(
          msalInstance,
          account,
          'frontend',
          undefined,
          async () => requestCountry(this.getFrontendToken())
        );
        const country = typeof retryResult === 'string' ? retryResult : '';
        this.setCountry(country);
        return country;
      }
      throw error;
    }
  }
}
